import { useRef, useMemo, useEffect, useCallback, useState } from 'react'
import { Group, Button } from '@mantine/core'
import IonIcon from '@reacticons/ionicons'
import CustomResizeableModal from 'app/views/components/modals/custom-resizeable-modal'
import ModalContentWrapper from 'app/views/components/modals/modal-content-wrapper'
import ModalButton from 'app/views/components/modals/modal-button'
import { ErrorNotification } from 'app/views/components/notifications/notification'
import { getDocumentFilename, extractFilenameDataFromRefinedTrade, isDraftSubTradeStatus } from 'app/utils/standardizedFilenames'
import { formatDateTimeLong } from 'app/utils/util-functions'
import { convertHtmlToPdfBlob } from 'app/utils/htmlToPdf'
import { RefinedSubTrade } from 'app/models/traded-facilities-params'
import UnappliedTransactionWarningModal from 'app/views/components/unapplied-transaction-warning/UnappliedTransactionWarningModal'
import { getFundingMemoMetadata, toggleFMMetadataLock } from 'app/state/ducks/signable-document/thunks'
import { addLockedFMMetadata, removeLockedFMMetadata } from 'app/state/ducks/signable-document/actions'
import { ErrorNotification as ErrorNotify, SuccessNotification } from 'app/views/components/notifications/notification'
import { useDispatch } from 'react-redux'

// Custom hook for document processing and cleanup.
// For most documents `documentUrl` is a ready-to-view PDF. For an UNLOCKED
// Funding Memo the backend sends live HTML (`previewHtml`); we convert it to a
// PDF blob client-side (pdfMake) so it renders in the iframe WITH the browser's
// native PDF toolbar — matching every other document. On conversion failure we
// fall back to showing the raw HTML.
const useDocumentProcessor = (documentUrl?: string, previewHtml?: string | null) => {
    const [documentBlobUrl, setDocumentBlobUrl] = useState<string | null>(documentUrl || null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        let createdBlobUrl: string | null = null;

        if (previewHtml) {
            setIsLoading(true);
            convertHtmlToPdfBlob(previewHtml)
                .then((blobUrl) => {
                    if (cancelled) {
                        URL.revokeObjectURL(blobUrl);
                        return;
                    }
                    createdBlobUrl = blobUrl;
                    setDocumentBlobUrl(blobUrl);
                })
                .catch(() => {
                    if (cancelled) return;
                    // Fallback: render the raw HTML (no toolbar) so the FM stays viewable.
                    const htmlBlob = new Blob([previewHtml], { type: 'text/html' });
                    createdBlobUrl = URL.createObjectURL(htmlBlob);
                    setDocumentBlobUrl(createdBlobUrl);
                })
                .finally(() => {
                    if (!cancelled) setIsLoading(false);
                });
        } else {
            setDocumentBlobUrl(documentUrl || null);
        }

        return () => {
            cancelled = true;
            if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
        };
    }, [documentUrl, previewHtml]);

    return {
        documentBlobUrl,
        isLoading,
    };
};


interface DocumentPreviewModalProps {
    opened: boolean
    onClose: () => void
    documentUrl?: string // For PDFs, this will be an object URL
    /**
     * Live-rendered HTML for an UNLOCKED Funding Memo. When set, the FM is shown
     * in real time (not a persisted DocuSeal PDF) and `documentUrl` is unused.
     */
    previewHtml?: string | null
    filename: string
    documentType?: string
    subTrade?: RefinedSubTrade | null
    onAction: (signAs: "buyer" | "seller" | "both") => void | Promise<void>
    possibleActions?: string[] // Optional, if you want to handle different actions
    hasUnappliedTransactions?: boolean
    onRedirectToSubTrade?: () => void
    /** Fired after FM lock/unlock — parent should refetch FM data. */
    onLockToggled?: () => void
}

export default function DocumentPreviewModal({
    opened,
    onClose,
    documentUrl,
    previewHtml,
    filename,
    documentType,
    subTrade,
    onAction,
    possibleActions = ['buyer', 'seller', 'both'],
    hasUnappliedTransactions = false,
    onRedirectToSubTrade,
    onLockToggled,
}: DocumentPreviewModalProps) {

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const { documentBlobUrl, isLoading } = useDocumentProcessor(documentUrl, previewHtml);

    const [showUnappliedWarning, setShowUnappliedWarning] = useState(false);
    const [pendingSignAction, setPendingSignAction] = useState<"buyer" | "seller" | "both" | null>(null);
    const [signingInProgress, setSigningInProgress] = useState(false);
    const [activeSignAction, setActiveSignAction] = useState<"buyer" | "seller" | "both" | null>(null);

    // FM locked status - fetched from /fm-metadata/one endpoint
    const [fmLockedInfo, setFmLockedInfo] = useState<{
        isLocked: boolean
        lockedAt: string | null
    }>({
        isLocked: false,
        lockedAt: null,
    })

    // Fetch FM metadata to check locked status (only for FM documents)
    useEffect(() => {
        if (documentType !== 'FundingMemoDocument' || !subTrade?.id || !opened) {
            setFmLockedInfo({ isLocked: false, lockedAt: null })
            return
        }

        getFundingMemoMetadata(subTrade.id).then((response) => {
            if (response.success && response.payload) {
                setFmLockedInfo({
                    isLocked: response.payload.lockedStatus,
                    lockedAt: response.payload.lockedStatus ? response.payload.createdAt : null,
                })
            } else {
                ErrorNotify({
                    title: `Failed to fetch FM metadata`,
                    message: response.payload || 'An error occurred.',
                })
                setFmLockedInfo({ isLocked: false, lockedAt: null })
            }
        })
    }, [documentType, subTrade?.id, opened])

    const standardizedFilename = useMemo(() => {
        if (documentType && subTrade) {
            const filenameData = extractFilenameDataFromRefinedTrade(subTrade);
            return getDocumentFilename(
                documentType,
                filenameData.dealName,
                filenameData.subtradeId,
                filenameData.buyerLegalName,
                isDraftSubTradeStatus(subTrade.status),
            );
        }
        return filename;
    }, [documentType, subTrade, filename]);

    const downloadDocument = useCallback(async () => {
        if (!documentBlobUrl) {
            ErrorNotification({
                title: 'Document failed to download',
                message: "Document is not available for download.",
            });
            return;
        }

        try {
            const response = await fetch(documentBlobUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = standardizedFilename.endsWith('.pdf')
                ? standardizedFilename
                : `${standardizedFilename}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);

        } catch (error) {
            console.error('Error downloading document:', error);
            ErrorNotification({
                title: 'Download Failed',
                message: 'Unable to download the document. Please try again.',
            });
        }
    }, [documentBlobUrl, standardizedFilename]);

    const executeSignAction = useCallback(async (signAs: "buyer" | "seller" | "both") => {
        setSigningInProgress(true);
        setActiveSignAction(signAs);
        try {
            await onAction(signAs);
        } finally {
            setSigningInProgress(false);
            setActiveSignAction(null);
        }
    }, [onAction]);

    const handleSignAction = useCallback((signAs: "buyer" | "seller" | "both") => {
        // Skip check for Affiliate Letter documents as they are auto-signed
        if (documentType === 'AffiliateLetterDocument') {
            executeSignAction(signAs);
            return;
        }

        if (hasUnappliedTransactions) {
            setPendingSignAction(signAs);
            setShowUnappliedWarning(true);
            return;
        }

        executeSignAction(signAs);
    }, [documentType, hasUnappliedTransactions, executeSignAction]);

    const handleWarningConfirm = useCallback(() => {
        if (pendingSignAction) {
            executeSignAction(pendingSignAction);
        }
        setShowUnappliedWarning(false);
        setPendingSignAction(null);
    }, [pendingSignAction, executeSignAction]);

    const handleWarningCancel = useCallback(() => {
        setShowUnappliedWarning(false);
        setPendingSignAction(null);
    }, []);

    return (
        <>
            <CustomResizeableModal
                opened={opened}
                onClose={onClose}
                initialWidth={1200}
                initialHeight={700}
                minWidth={800}
                minHeight={500}
            >
                {({ onMaximize, isMaximized }) => (
                    <ModalContentWrapper
                        title='View Documents'
                        subTitle={subTrade?.systemId}
                        withCloseIcon
                        closeModal={onClose}
                        withMaximizeButton
                        onMaximize={onMaximize}
                        isMaximized={isMaximized}
                        banner={<LockedFMDocumentBanner
                            onClose={onClose}
                            documentType={documentType}
                            fmLockedInfo={fmLockedInfo}
                            subTradeId={subTrade?.id}
                            onLockStatusChange={(isLocked, lockedAt) => {
                                setFmLockedInfo({ isLocked, lockedAt })
                                onLockToggled?.()
                            }}
                        />}
                    >
                        <div className='document-preview-modal-content'>
                            <div className='document-preview-modal-file-frame'>
                                <span className='document-preview-modal-filename'>{standardizedFilename}</span>
                                <ModalButton variant='outline' onClick={downloadDocument}>
                                    Download PDF
                                </ModalButton>
                            </div>
                            <div className='pdf-preview-container'>
                                {isLoading && (
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: '100%',
                                        height: '100%',
                                        fontSize: '14px',
                                        color: '#999'
                                    }}>
                                        Converting document to PDF...
                                    </div>
                                )}
                                {!isLoading && documentBlobUrl && (
                                    <iframe
                                        ref={iframeRef}
                                        src={documentBlobUrl}
                                        title='Document Preview'
                                        style={{ width: '100%', height: '100%', border: 'none' }}
                                    />
                                )}
                            </div>
                            <div className='document-preview-buttons-container'>
                                <Group spacing='xs' position='right'>
                                    <ModalButton variant='outline' onClick={onClose}>Cancel</ModalButton>
                                    {/* Hide signing buttons for Affiliate Letter documents since they are auto-signed by buyer and need no further signatures */}
                                    {documentType !== 'AffiliateLetterDocument' && (
                                        <>
                                            <ModalButton variant='primary' onClick={() => handleSignAction("seller")} disabled={!possibleActions.includes("seller") || signingInProgress} loading={activeSignAction === "seller"}>Sign as Seller</ModalButton>
                                            <ModalButton variant='primary' onClick={() => handleSignAction("buyer")} disabled={!possibleActions.includes("buyer") || signingInProgress} loading={activeSignAction === "buyer"}>Sign as Buyer</ModalButton>
                                            <ModalButton variant='primary' onClick={() => handleSignAction("both")} disabled={!possibleActions.includes("both") || signingInProgress} loading={activeSignAction === "both"}>Sign as Both</ModalButton>
                                        </>
                                    )}
                                </Group>
                            </div>
                        </div>
                    </ModalContentWrapper>
                )}
            </CustomResizeableModal>

            {showUnappliedWarning && (
                <UnappliedTransactionWarningModal
                    onConfirm={handleWarningConfirm}
                    onCancel={handleWarningCancel}
                    onRedirectToSubTrade={onRedirectToSubTrade}
                />
            )}
        </>
    )
}

interface LockedFMDocumentBannerProps {
    onClose: () => void
    documentType: string | undefined
    fmLockedInfo: { isLocked: boolean; lockedAt: string | null }
    subTradeId: string | undefined
    onLockStatusChange: (isLocked: boolean, lockedAt: string | null) => void
}

function LockedFMDocumentBanner({ onClose, documentType, fmLockedInfo, subTradeId, onLockStatusChange }: LockedFMDocumentBannerProps) {
    const [isToggling, setIsToggling] = useState(false)
    const dispatch = useDispatch()

    if (documentType !== 'FundingMemoDocument') {
        return null
    }

    const handleToggleLock = async (action: 'lock' | 'unlock') => {
        if (!subTradeId || isToggling) return

        setIsToggling(true)
        const response = await toggleFMMetadataLock(subTradeId, action)
        setIsToggling(false)

        if (response.success) {
            if (action === 'lock') {
                dispatch(addLockedFMMetadata(response.payload))
                onLockStatusChange(true, response.payload?.createdAt || new Date().toISOString())
                SuccessNotification({
                    title: 'FM Locked',
                    message: 'Funding Memo has been locked successfully.',
                })
            } else {
                dispatch(removeLockedFMMetadata(subTradeId))
                onLockStatusChange(false, null)
                SuccessNotification({
                    title: 'FM Unlocked',
                    message: 'Funding Memo has been unlocked successfully.',
                })
            }
            onClose();
        } else {
            ErrorNotify({
                title: `Failed to ${action} FM`,
                message: response.payload || 'An error occurred.',
            })
        }
    }

    const { isLocked, lockedAt } = fmLockedInfo

    return (
        <Group style={{
            backgroundColor: 'FFF7ED',
            padding: '4px 6px 4px 16px',
            borderRadius: '10px',
            alignItems: 'center',
            border: '2px solid #FF8904',
            width: '370px',
            justifyContent: 'space-between',
        }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ color: '#7E2A0C', fontWeight: 600, fontSize: '14px' }}>Document {isLocked ? 'Locked' : 'Unlocked'}</span>
                {isLocked && (
                    <span style={{ color: '#CA3500', fontSize: '12px' }}>
                        Locked on {formatDateTimeLong(lockedAt ? new Date(lockedAt) : null)}
                    </span>
                )}
            </div>
            <Button
                bg="#32439A"
                size="lg"
                radius="8px"
                onClick={() => handleToggleLock(isLocked ? 'unlock' : 'lock')}
                loading={isToggling}
            >
                <IonIcon name={!isLocked ? 'lock-closed' : 'lock-open'} style={{ marginRight: '8px' }} />
                {isLocked ? 'Unlock' : 'Lock'}
            </Button>
        </Group>
    )
}